import { COUNTRY_CODES } from "../../../../constants/countries";

/**
 * La ubicación del comprador, que Hacienda exige y que sale de un solo dato.
 *
 * CertificaE no lee provincia, cantón ni distrito de ningún catálogo: los
 * **recorta del código postal** del documento, que en Costa Rica es `PCCDD`
 * --provincia en el primer dígito, cantón en los dos siguientes y distrito en
 * los dos últimos--. `OtrasSenas` la arma con la dirección y la dirección 2 **de
 * la ficha del cliente**, no del documento. Con el código vacío el comprobante
 * sale con `<Provincia />` vacío y Hacienda lo rechaza: le pasó a `VFR15-009033`,
 * de un cliente dado de alta desde el terminal, el 13/09/2026.
 *
 * Acuerdo `contrato-ubicacion-y-correo-fe-cri-2026-09-13.md` (filas T2, T3 y T5),
 * leído del código de San-CertificaE-CR 1.0.0.6 (`CU-CRBuidlXML.al:264-270`,
 * `:362-366`, `:550-651`).
 */

export const PAIS_COSTA_RICA = "CR";

/** Provincia de 1 a 7; cantón y distrito, nunca `00`. */
export const CODIGO_POSTAL_DE_COSTA_RICA = /^[1-7](?!00)\d{2}(?!00)\d{2}$/;

/** Lo que admite el receptor en `OtrasSenas`. */
export const OTRAS_SENAS_MINIMO = 5;
export const OTRAS_SENAS_MAXIMO = 160;

const texto = (valor) => String(valor ?? "").trim();

export const esCodigoPostalDeCostaRica = (valor) => CODIGO_POSTAL_DE_COSTA_RICA.test(texto(valor));

/**
 * Lo que está mal en un código postal, o null.
 *
 * Sólo se exige a un cliente de Costa Rica: un cliente de otro país no lleva
 * ubicación en la factura ni en el tiquete. La nota de crédito es la excepción,
 * y la decide `revisarUbicacion`, no esta regla de captura.
 *
 * @param {string} codigo
 * @param {string} pais El `Pais` del cliente.
 */
export const revisarCodigoPostal = (codigo, pais) => {
  if (texto(pais).toUpperCase() !== PAIS_COSTA_RICA) return null;

  const crudo = texto(codigo);
  if (!crudo) {
    return "Falta el código postal. Hacienda saca de ahí la provincia, el cantón y el distrito: son 5 dígitos, por ejemplo 10101.";
  }
  if (!esCodigoPostalDeCostaRica(crudo)) {
    return `«${crudo}» no es un código postal de Costa Rica: son 5 dígitos, la provincia (1 a 7), el cantón y el distrito.`;
  }
  return null;
};

/**
 * Lo que está mal en la dirección que viaja como `OtrasSenas`, o null.
 *
 * @param {string} direccion   `Direccion` de la ficha.
 * @param {string} direccion2  `Direccion_2` de la ficha.
 */
export const revisarOtrasSenas = (direccion, direccion2) => {
  const juntas = `${texto(direccion)} ${texto(direccion2)}`.trim();

  if (juntas.length < OTRAS_SENAS_MINIMO) {
    return `La dirección necesita al menos ${OTRAS_SENAS_MINIMO} caracteres: viaja al comprobante como «otras señas».`;
  }
  if (juntas.length > OTRAS_SENAS_MAXIMO) {
    return `La dirección y la dirección 2 juntas pasan de ${OTRAS_SENAS_MAXIMO} caracteres, que es el máximo del receptor.`;
  }
  return null;
};

/**
 * La copia que se congela en la cabecera al elegir, dar de alta o confirmar al
 * cliente. Fuera de Costa Rica no se escribe nada.
 *
 * Se congela por la misma razón que el tipo de comprobante: si la edición del
 * cliente la rechaza Business Central, la ficha de BC se queda con el código
 * viejo, y la factura lo heredaría. Con la copia, el documento lleva el suyo.
 *
 * @param {string} countryCode
 * @param {{Cod_Postal?: string, Pais?: string}} cliente
 */
export const ubicacionParaLaCabecera = (countryCode, cliente) => {
  if (countryCode !== COUNTRY_CODES.COSTARICA) return {};
  return {
    Sell_to_Post_Code: texto(cliente?.Cod_Postal),
    Sell_to_Country_Region_Code: texto(cliente?.Pais).toUpperCase(),
  };
};

/**
 * Las claves de ubicación que viajan en `insertaFacturasVenta`.
 *
 * - El código postal, sólo si tiene forma de código de Costa Rica.
 * - El país, **sólo si vale `CR`**. BC lo valida contra `Country/Region` después
 *   de insertar el borrador (`Importaciones.al:2346`), así que un país que no
 *   exista dejaría un borrador vacío y un reintento sin fin.
 *
 * Una clave que no viaja no es un vacío: BC toma el dato de la ficha del cliente
 * (`Importaciones.al:2551-2562`), que es lo que hacía antes de esto.
 *
 * @param {object} documento La cabecera local.
 */
export const clavesDeUbicacionParaBC = (documento) => {
  const claves = {};
  const codigo = texto(documento?.Sell_to_Post_Code);
  if (esCodigoPostalDeCostaRica(codigo)) claves.Sell_to_Post_Code = codigo;
  if (texto(documento?.Sell_to_Country_Region_Code).toUpperCase() === PAIS_COSTA_RICA) {
    claves.Sell_to_Country_Region_Code = PAIS_COSTA_RICA;
  }
  return claves;
};

/**
 * Lo que BC va a tener al registrar: lo del documento si viaja y, si no, lo de
 * la ficha. Es lo que hay que revisar, porque es lo que lee CertificaE.
 *
 * Sin ficha se usa lo que tenga el documento.
 *
 * @param {object} documento La cabecera local.
 * @param {object} [ficha]   El cliente local.
 * @returns {{codigoPostal: string, pais: string, direccion: string, direccion2: string}}
 */
export const ubicacionQueRecibeBC = (documento, ficha) => {
  const claves = clavesDeUbicacionParaBC(documento);

  const codigoPostal =
    claves.Sell_to_Post_Code ?? texto(ficha ? ficha.Cod_Postal : documento?.Sell_to_Post_Code);

  const pais =
    claves.Sell_to_Country_Region_Code ??
    texto(
      ficha
        ? ficha.Pais
        : documento?.Sell_to_Country_Region_Code || documento?.Bill_to_Country_Region_Code
    ).toUpperCase();

  return {
    codigoPostal,
    pais,
    direccion: texto(ficha ? ficha.Direccion : documento?.Bill_to_Address),
    direccion2: texto(ficha ? ficha.Direccion_2 : documento?.Direccion_2),
  };
};
